import { Link } from '@tanstack/react-router';
import { ChevronRight, Flag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';
import { formatDurationMs } from '~/utils/date';

import { useNowNext } from '../hooks/use-now-next';
import { useRun } from '../hooks/use-run';
import { useRunLabels } from '../hooks/use-run-labels';
import type { RunProgress } from '../types/run.api.types';
import { catalogLabel } from '../utils/catalog-labels';
import { decisionSearch } from '../utils/run-decisions';
import { STATE_ICON } from '../utils/run-phase-icons';
import {
  currentPhaseIndex,
  hasProgressGatedStages,
  type PhaseState,
  type PhaseSummary,
  reached,
  resolveGatedStages,
  summarizePhases,
} from '../utils/run-phase-summary';

import { NowNext } from './run-now-next';

/** Progress bar colour under each phase name — the only place state reads as colour (G1). */
const BAR_CLASS: Record<PhaseState, string> = {
  done: 'bg-success',
  skipped: 'bg-border',
  running: 'bg-info',
  waiting: 'bg-warning',
  failed: 'bg-error',
  notStarted: 'bg-border',
};

/** Phase name tone — only the phase that needs the user is coloured in (G1). */
const NAME_CLASS: Record<PhaseState, string> = {
  done: 'text-foreground',
  skipped: 'text-muted-foreground',
  running: 'text-info',
  waiting: 'text-warning',
  failed: 'text-error',
  notStarted: 'text-muted-foreground',
};

interface Props {
  runId: string;
  progress: RunProgress;
  /** Show the Now / Next sentence under the strip (default true); it is always the strip's
   *  tooltip too, so a caller that hides it (the run shell) still keeps it one hover away. */
  showNow?: boolean;
  className?: string | undefined;
}

/**
 * Run progress as design B's stepper (R2): the four phases laid out in a grid that WRAPS at
 * narrow widths instead of scrolling — never a horizontal scrollbar, never a truncated phase
 * name. Every phase carries one caption line (what it did, or what it will do); the phase with
 * an open decision additionally names its stage and how long it has waited, inline — no popover,
 * no hover-only tooltip over the strip. Full per-attempt detail lives only in the Overview's
 * progress card (click a stage there), never floated here. Now / Next sits on its own line right
 * under the grid (or, where a caller hides it, in the strip's tooltip and the header's status
 * popover). A stopped batch's failure reason is NOT repeated here (design round 2 §1A) — it is
 * the header's own `RunFailureSummary` status section, shown once, above this strip.
 * Shared by the run header and the experiment header (both render this one component).
 */
export function RunPhaseStrip({ runId, progress, showNow = true, className }: Props) {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCatalog } = useTranslation('catalog-v2');
  // Fallback for backends without §49 `progress.gatedStages`: read the run's flow config.
  const { data: run } = useRun(runId, !hasProgressGatedStages(progress));
  const phases = summarizePhases(progress, resolveGatedStages(progress, run));
  const current = currentPhaseIndex(phases, progress.currentStage);
  const isGated = (s: string) => decisionStagesOf(phases).has(s);
  const { now, next } = useNowNext({ progress, isGated });
  const nowNextText = [
    `${t('phaseTimeline.nowLabel')} ${now}`,
    next ? `${t('phaseTimeline.nextLabel')} ${next}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  // DELIVERY reads "Publish to Google Merchant Center" wherever this strip names it.
  const destination = progress.connectorCode
    ? catalogLabel(tCatalog, 'connector', progress.connectorCode)
    : null;

  if (phases.length === 0) return null;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <ol
        aria-label={tCatalog('phases.ariaLabel')}
        title={nowNextText}
        className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-4"
      >
        {phases.map((p, i) => (
          <li key={p.phase}>
            <PhaseBlock
              runId={runId}
              phase={p}
              isCurrent={i === current && p.state !== 'done'}
              waitingMs={p.openDecision ? (progress.pendingGate?.waitingMs ?? null) : null}
              destination={destination}
            />
          </li>
        ))}
      </ol>
      {showNow ? <NowNext progress={progress} isGated={isGated} /> : null}
    </div>
  );
}

/**
 * The strip's "Now: … · Next: …" sentence on its own — for a click-open surface (the header's
 * status popover) where a run shell hides it under the strip, so it is never hover-only.
 */
export function RunNowNext({ runId, progress }: { runId: string; progress: RunProgress }) {
  const { data: run } = useRun(runId, !hasProgressGatedStages(progress));
  const phases = summarizePhases(progress, resolveGatedStages(progress, run));
  return <NowNext progress={progress} isGated={(s) => decisionStagesOf(phases).has(s)} />;
}

/** Every stage any phase marks as a decision point — the set `NowNext` checks "Next" against. */
function decisionStagesOf(phases: PhaseSummary[]): Set<string> {
  return new Set(phases.flatMap((p) => p.decisions));
}

/** One phase's link target, from the stage(s) it has actually reached — null when the phase
 *  hasn't started yet (design round 2 §PS: a phase not reached is not a link). Every branch
 *  carries a `search` key (undefined where the target route takes none) so the one call site can
 *  read `link.search` without narrowing on which phase it came from. */
function phaseLink(p: PhaseSummary) {
  if (p.state === 'notStarted') return null;
  const by = (code: string) => p.stages.find((s) => s.stage === code);
  if (p.phase === 'PREPARE') {
    return { to: '/catalog-v2/runs/$runId/products' as const, search: undefined };
  }
  if (p.phase === 'CHECK_QUALITY') {
    // SAMPLE is the phase's later stage — link there once it's reached, else PRE_ENRICH_SCORE.
    const stage = reached(by('SAMPLE'))
      ? 'SAMPLE'
      : reached(by('PRE_ENRICH_SCORE'))
        ? 'PRE_ENRICH_SCORE'
        : null;
    return stage
      ? { to: '/catalog-v2/runs/$runId/products' as const, search: decisionSearch(stage) }
      : null;
  }
  if (p.phase === 'GENERATE') {
    return reached(by('ENRICHMENT'))
      ? {
          to: '/catalog-v2/runs/$runId/products' as const,
          search: decisionSearch('ENRICHMENT'),
        }
      : null;
  }
  // Summary's Approval / Publish stage panes cover the open decision and the delivery results
  // (the Publishing tab folded into Summary): Publish once delivery started, else Approval.
  return {
    to: '/catalog-v2/runs/$runId' as const,
    search: { fStage: reached(by('DELIVERY')) ? 'DELIVERY' : 'APPROVAL' },
  };
}

interface PhaseBlockProps {
  runId: string;
  phase: PhaseSummary;
  isCurrent: boolean;
  waitingMs: number | null;
  /** Resolved connector name, so the SHIP phase's DELIVERY caption can read "Publish to X". */
  destination: string | null;
}

/**
 * One phase, two lines, never truncated or scrolled: icon + name + a state-coloured progress bar on
 * one row, then ONE caption line (what happened, or what will) — for the phase with an open
 * decision, the stage waiting on you plus how long it has waited, after a "·". Design
 * round 2 §PS: a phase already reached is a link to its results (Products + its decision panel /
 * Summary's Approval / Publish pane), the whole block its hit area, with a visible "›" and a
 * hover/focus lift; a phase not reached yet renders the same content but inert (muted,
 * `cursor-not-allowed`).
 */
function PhaseBlock({ runId, phase: p, isCurrent, waitingMs, destination }: PhaseBlockProps) {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCatalog } = useTranslation('catalog-v2');
  const { stageLabel } = useRunLabels();
  const { Icon, className: iconClass } = STATE_ICON[p.state];
  const name = tCatalog(`phases.${p.phase}`);
  const lastFlow = p.flow.filter((s) => !s.aside).at(-1);
  const link = phaseLink(p);

  /** What this phase's caption line says: the open decision's stage, else its flow, else a plain
   *  state word (not started / skipped) so the line is never empty and never guesses a number. */
  const caption = p.openDecision
    ? stageLabel(p.openDecision, destination)
    : lastFlow
      ? t(`phaseTimeline.flow.${lastFlow.key}`, { count: lastFlow.count })
      : p.state === 'skipped'
        ? t('phaseTimeline.state.skipped')
        : p.state === 'notStarted'
          ? t(`phaseTimeline.upcoming.${p.phase}`)
          : null;

  // One sub-line max: the caption, with the open decision's wait folded in after a "·".
  const waited =
    p.openDecision && waitingMs
      ? t('phaseTimeline.waited', { time: formatDurationMs(waitingMs) })
      : null;

  // One row: icon · name · ⚑ · state bar · ›, then the single caption line under it.
  const content = (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon aria-hidden="true" className={cn('size-3.5 shrink-0', iconClass)} />
        <span className={cn('shrink-0 text-sm font-medium whitespace-nowrap', NAME_CLASS[p.state])}>
          {name}
        </span>
        {p.decisions.length > 0 ? (
          <Flag
            aria-label={t('phaseTimeline.decisionPoint')}
            className={cn(
              'size-3 shrink-0',
              p.openDecision ? 'text-warning' : 'text-muted-foreground',
            )}
          />
        ) : null}
        <span
          aria-hidden="true"
          className={cn(
            'h-1 min-w-6 flex-1 rounded-full transition-[height]',
            BAR_CLASS[p.state],
            link && 'group-hover:h-1.5 group-focus-visible:h-1.5',
          )}
        />
        {link ? (
          <ChevronRight aria-hidden="true" className="text-muted-foreground size-3.5 shrink-0" />
        ) : null}
      </div>
      {caption || waited ? (
        <p className="text-muted-foreground min-w-0 text-xs break-words whitespace-normal tabular-nums">
          {caption}
          {caption && waited ? ' · ' : null}
          {waited ? <span className="text-warning font-medium">{waited}</span> : null}
        </p>
      ) : null}
    </>
  );

  const className = cn(
    'group flex min-w-0 flex-col gap-1 rounded-lg p-1.5 -m-1.5',
    link
      ? 'cursor-pointer transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none'
      : 'cursor-not-allowed opacity-80',
  );

  if (link) {
    return (
      <Link
        to={link.to}
        params={{ runId }}
        {...(link.search ? { search: link.search } : {})}
        className={className}
        aria-current={isCurrent ? 'step' : undefined}
      >
        {content}
      </Link>
    );
  }
  return (
    <div className={className} aria-current={isCurrent ? 'step' : undefined} aria-disabled="true">
      {content}
    </div>
  );
}
